import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { v4 } from 'uuid';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import { DataGrid } from '@mui/x-data-grid';
import { DEFAULT_ICON_CLASS_NAME } from '../../../theme/constants/icon';
import { DETAILED_DATA_GRID_CLASS_NAME } from '../../../theme/constants/data-grid';
import { formatDayMonthYear, formatMoneyWithSign } from '../../../helpers';
import DataGridRenderHeader from '../../../components/dataGrids/DataGridRenderHeader';
import CollapsibleSection from './CollapsibleSection';

const EmptyPayments = () => {
  const theme = useTheme();
  const InformationIcon = theme.branding.icons.information;

  return (
    <Stack alignItems="center" spacing={1} py={3}>
      <InformationIcon className={DEFAULT_ICON_CLASS_NAME} sx={{ width: 40, height: 'auto' }} />
      <Typography variant="small_2" color="text.secondary">
        Aún no existen pagos
      </Typography>
    </Stack>
  );
};

const PAYMENT_COLUMNS = [
  {
    field: 'paymentDate',
    headerName: 'Fecha de pago',
    flex: 1,
    minWidth: 120,
    sortable: false,
    renderHeader: DataGridRenderHeader,
    renderCell: ({ row }) => (
      <Typography variant="small_2" color="gray.A700">{formatDayMonthYear(row.paymentDate) || '-'}</Typography>
    ),
  },
  {
    field: 'defaultDays',
    headerName: 'Días de mora',
    flex: 1,
    minWidth: 110,
    sortable: false,
    renderHeader: DataGridRenderHeader,
    renderCell: ({ row }) => (
      <Typography variant="small_2" color="gray.A700">
        {row.defaultDays} {row.defaultDays === 1 ? 'día' : 'días'}
      </Typography>
    ),
  },
  {
    field: 'amountPaid',
    headerName: 'Monto abonado',
    flex: 1,
    minWidth: 120,
    sortable: false,
    align: 'right',
    headerAlign: 'right',
    renderHeader: DataGridRenderHeader,
    renderCell: ({ row }) => (
      <Typography variant="small_2" fontWeight={600} color="gray.A700">
        {formatMoneyWithSign(row.amountPaid)}
      </Typography>
    ),
  },
];

const InvoicePaymentsSection = ({ payments }) => {
  // Payments have no backend id and can repeat (same date/amount/days), so assign a stable
  // id per row once. Memoized on `payments` (a stable Apollo reference), ids survive
  // re-renders and only change when the payment list itself changes.
  const rows = useMemo(
    () => payments.map((payment) => ({ id: v4(), ...payment })),
    [payments],
  );

  return (
    <CollapsibleSection title="Pagos" id="invoice-payments-section">
      {rows.length === 0 ? (
        <EmptyPayments />
      ) : (
        <DataGrid
          className={DETAILED_DATA_GRID_CLASS_NAME}
          columns={PAYMENT_COLUMNS}
          rows={rows}
          autoHeight
          hideFooter
          disableColumnMenu
          disableSelectionOnClick
          rowHeight={48}
          sx={{ mt: 2 }}
        />
      )}
    </CollapsibleSection>
  );
};

InvoicePaymentsSection.propTypes = {
  payments: PropTypes.arrayOf(
    PropTypes.shape({
      paymentDate: PropTypes.string,
      defaultDays: PropTypes.number,
      amountPaid: PropTypes.shape({}),
    }),
  ),
};

InvoicePaymentsSection.defaultProps = {
  payments: [],
};

export default InvoicePaymentsSection;
